// Builder SAVE cluster: one PRIMARY button (chosen by ownership) + a "Save ▾" menu for the less-frequent
// actions. Decluttered replacement for the old three separate save-ish buttons (Template / Update template /
// Save As). Pure decision lives in saveToolbarLayout.ts; the actual dialogs/backend are unchanged — this
// component only decides what to show and emits an action kind.

import React, { useState } from 'react';
import { Save, Upload, ChevronDown, Copy, Book } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { saveToolbarLayout, type SaveActionKind, type SaveToolbarState } from './saveToolbarLayout';

const ICONS: Record<SaveActionKind, React.ReactNode> = {
  'update-template': <Upload className="w-4 h-4" />,
  'save-strategy': <Save className="w-4 h-4" />,
  'save-as-copy': <Copy className="w-4 h-4" />,
  'publish-template': <Book className="w-4 h-4" />,
};

const TITLES: Record<SaveActionKind, string> = {
  'update-template': 'Overwrite the loaded template you own with the current canvas',
  'save-strategy': 'Save this strategy to your account',
  'save-as-copy': 'Save the current canvas as a NEW personal copy (leaves the loaded artifact intact)',
  'publish-template': 'Publish the current canvas as a categorized community/starter template',
};

export const SaveActions: React.FC<{
  state: SaveToolbarState;
  disabled?: boolean;
  busyLabel?: string | null;     // e.g. "Saving…" — overrides the primary label while in flight
  onAction: (kind: SaveActionKind) => void;
}> = ({ state, disabled, busyLabel, onAction }) => {
  const [open, setOpen] = useState(false);
  useEscapeKey(() => setOpen(false), open);
  const layout = saveToolbarLayout(state);
  const isUpdate = layout.primary.kind === 'update-template';
  const primaryColor = disabled
    ? 'bg-gray-400 cursor-not-allowed'
    : isUpdate ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700';

  return (
    <div className="relative flex items-stretch">
      <button
        type="button"
        onClick={() => onAction(layout.primary.kind)}
        disabled={disabled}
        title={TITLES[layout.primary.kind]}
        className={`flex items-center px-3 py-1.5 text-white transition-colors ${primaryColor} ${layout.menu.length > 0 ? 'rounded-l-md' : 'rounded-md'}`}
      >
        <span className="mr-1.5">{ICONS[layout.primary.kind]}</span>
        {busyLabel ?? layout.primary.label}
      </button>

      {layout.menu.length > 0 && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          disabled={disabled}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="More save options"
          title="More save options"
          className={`flex items-center px-1.5 rounded-r-md text-white border-l border-white/25 transition-colors ${primaryColor}`}
        >
          <ChevronDown className="w-4 h-4" />
        </button>
      )}

      {open && layout.menu.length > 0 && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div role="menu" className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-md shadow-lg py-1 min-w-[190px]">
            {layout.menu.map((m) => (
              <button
                key={m.kind}
                type="button"
                role="menuitem"
                onClick={() => { setOpen(false); onAction(m.kind); }}
                title={TITLES[m.kind]}
                className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
              >
                {ICONS[m.kind]}
                {m.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default SaveActions;
